import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, XCircle, ArrowLeft, ArrowRight } from "@phosphor-icons/react";

export default function QuizPlay() {
  const { qid } = useParams();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get(`/quizzes/${qid}`).then((r) => setQuiz(r.data)); }, [qid]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/quizzes/${qid}/attempt`, { quiz_id: qid, answers });
      setResult(data);
      toast.success(`Score: ${data.score}/${data.total} (${data.percent}%)`);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Could not submit");
    }
    setBusy(false);
  };

  const reset = () => { setAnswers({}); setResult(null); };

  if (!quiz) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-8">
      <Link to="/app/quizzes" className="text-sm text-muted-foreground inline-flex items-center gap-2 hover:text-foreground" data-testid="quiz-back">
        <ArrowLeft size={14} /> All quizzes
      </Link>

      <header className="fade-up">
        <div className="overline mb-3">Quiz</div>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tighter leading-[0.95]">{quiz.title}</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">{quiz.description}</p>
      </header>

      {result && (
        <div className="border border-primary bg-primary/5 p-6 fade-up" data-testid="quiz-result">
          <div className="overline">Result</div>
          <div className="font-display text-5xl mt-2 mb-3">{result.percent}% · {result.score}/{result.total}</div>
          <button onClick={reset} data-testid="quiz-retake" className="text-sm border border-primary text-primary px-4 py-2 hover:bg-primary hover:text-primary-foreground transition-colors">
            Retake
          </button>
        </div>
      )}

      <div className="space-y-6">
        {quiz.questions.map((q, i) => {
          const selected = answers[q.id];
          const r = result?.results?.find((x) => x.id === q.id);
          return (
            <div key={q.id} className="border border-border bg-white p-6" data-testid={`quiz-q-${q.id}`}>
              <div className="flex items-start gap-3">
                <div className="font-mono text-xs bg-secondary px-2 py-0.5 mt-1">{String(i + 1).padStart(2, "0")}</div>
                <div className="flex-1">
                  <div className="text-lg font-medium mb-4">{q.q}</div>
                  <div className="space-y-2">
                    {q.options.map((opt, idx) => {
                      const chosen = selected === idx;
                      let cls = "border-border hover:border-foreground";
                      if (result) {
                        if (idx === r.correct) cls = "border-primary bg-primary/10";
                        else if (chosen && !r.is_correct) cls = "border-accent bg-accent/10";
                        else cls = "border-border opacity-70";
                      } else if (chosen) {
                        cls = "border-foreground bg-secondary";
                      }
                      return (
                        <button key={idx} onClick={() => !result && setAnswers((a) => ({ ...a, [q.id]: idx }))}
                          disabled={!!result}
                          data-testid={`quiz-opt-${q.id}-${idx}`}
                          className={`w-full text-left px-4 py-3 border text-sm flex items-center gap-3 transition-colors ${cls}`}>
                          <span className="font-mono text-xs w-6">{String.fromCharCode(65 + idx)}</span>
                          <span className="flex-1">{opt}</span>
                          {result && idx === r.correct && <CheckCircle size={16} weight="fill" className="text-primary" />}
                          {result && chosen && !r.is_correct && <XCircle size={16} weight="fill" className="text-accent" />}
                        </button>
                      );
                    })}
                  </div>
                  {result && (
                    <div className="mt-4 text-sm p-3 bg-secondary border-l-2 border-primary">
                      <div className="overline mb-1">Explanation</div>
                      {r.explain}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!result && (
        <div className="flex items-center justify-between border-t border-border pt-6">
          <div className="text-sm text-muted-foreground">
            {Object.keys(answers).length} / {quiz.questions.length} answered
          </div>
          <button onClick={submit} disabled={busy || Object.keys(answers).length !== quiz.questions.length}
            data-testid="quiz-submit"
            className="px-5 py-3 bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2 disabled:opacity-50 hover:bg-[color:hsl(223_100%_28%)] transition-colors">
            {busy ? "Submitting…" : (<>Submit quiz <ArrowRight size={14} weight="bold" /></>)}
          </button>
        </div>
      )}
    </div>
  );
}
